import {
  ClientResponse,
  ConversationEventType,
  ConversationMessage,
  ConversationParticipant,
  MessagePartSubmitParams,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataConversation,
  ConversationsApiDataMessagePart,
  ConversationsApiSendMessageEndpointRequest,
} from '../generated/types';
import {ErrorResponse, ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';
import {PageContextNormalizer} from './PageContextNormalizer';
import {
  buildCachedAnsweredOptionsPromptMessage,
  buildDeltaResponseState,
  emitAddedMessages,
  emitDeltaResponse,
  selectedOptionIdFromSubmissionParts,
} from './SendResponseHandling';

type SubmitMessagePartResponse =
  | {type: 'success'; data?: ConversationsApiDataConversation}
  | ErrorResponse;

type DeleteConversationResponse =
  | {type: 'success'; data: undefined}
  | {type: 'error'; error: string; code: number};

type SubmitMessagePartHandlerDeps = {
  sendMessage: (
    conversationId: string,
    payload: ConversationsApiSendMessageEndpointRequest,
    options?: {allowNoContent?: boolean},
  ) => Promise<SubmitMessagePartResponse>;
  deleteConversation?: (
    conversationId: string,
  ) => Promise<DeleteConversationResponse>;
  emitConversationEvent: (event: ConversationEventType) => void;
  getParticipant: () => ConversationParticipant | undefined;
  reportWarning?: ReportWarning;
  enableUhPendingInput?: boolean;
  attachmentDownloadPath?: string;
  getCachedMessage?: (
    conversationId: string,
    messageId: string,
  ) => ConversationMessage | undefined;
};

export class SubmitMessagePartHandler {
  private readonly deps: SubmitMessagePartHandlerDeps;

  constructor(deps: SubmitMessagePartHandlerDeps) {
    this.deps = deps;
  }

  /**
   * Submit-as-message intentionally bypasses SendConversationMessageHandler.
   *
   * That handler translates framework ConversationMessage variants into the API
   * send-message payload, but message part submissions need to send raw message
   * parts such as `selected_option` or form-specific submission parts that are
   * not representable by the shared ConversationMessage union. This handler
   * therefore works directly in terms of the `/messages` endpoint contract.
   */
  async call(params: MessagePartSubmitParams): Promise<ClientResponse> {
    const participant = this.deps.getParticipant();
    if (!participant?.id) {
      return {code: 400, error: 'Missing participant'};
    }
    if (!params.submission.id) {
      return {code: 400, error: 'Missing message id'};
    }
    if (params.submission.parts.length === 0) {
      return {code: 400, error: 'Missing message parts'};
    }

    const response = await this.deps.sendMessage(
      params.conversationId,
      SubmitMessagePartHandler.buildPayload(params, participant),
      {allowNoContent: true},
    );

    if (response.type === 'error') {
      return FrameworkAdapter.toClientErrorResponse(response);
    }
    if (!response.data) {
      return {code: 200};
    }

    const {submission} = params;
    const {
      context,
      ended,
      messages: newMessages,
    } = buildDeltaResponseState({
      conversationId: params.conversationId,
      apiMessages: response.data.messages,
      participantsById: {
        [participant.id]: participant,
      },
      user: participant,
      includeUserMessages: true,
      attachmentDownloadPath: this.deps.attachmentDownloadPath,
      reportWarning: this.deps.reportWarning,
    });
    const promptUpdate = buildCachedAnsweredOptionsPromptMessage({
      conversationId: params.conversationId,
      creatorPartId: submission.creatorPartId,
      getCachedMessage: this.deps.getCachedMessage,
      participant,
      selectedOptionId: selectedOptionIdFromSubmissionParts(
        submission.parts as ConversationsApiDataMessagePart[],
      ),
    });

    // A discrete option selection is fully represented by the prompt-answered
    // update. v1 parity (universal-help client.ts) emits no separate user
    // bubble for a button/select option, and the full-conversation refetch
    // (FrameworkAdapter.applySelectedOptionParts) likewise collapses the
    // selection into the prompt — so suppressing the echo here keeps the live
    // view identical to a later refetch. Text / attachment / form submissions
    // keep their echo (matching both v1 and the refetch path).
    const isDiscreteOptionSelection = submission.parts.some(
      (part) => part.type === 'selected_option',
    );
    const messagesToEmit =
      promptUpdate && isDiscreteOptionSelection
        ? newMessages.filter(
            (message) => message.participant.id !== participant.id,
          )
        : newMessages;

    await emitDeltaResponse({
      emitConversationEvent: this.deps.emitConversationEvent,
      conversationId: params.conversationId,
      promptUpdate,
      context,
      ended,
      deleteConversation: this.deps.deleteConversation,
      typingEndedParticipantId: participant.id,
      reportWarning: this.deps.reportWarning,
      emitMessages: () =>
        emitAddedMessages(
          this.deps.emitConversationEvent,
          params.conversationId,
          messagesToEmit,
        ),
    });

    return {code: 200};
  }

  private static buildPayload(
    params: MessagePartSubmitParams,
    participant: ConversationParticipant,
  ): ConversationsApiSendMessageEndpointRequest {
    const {submission} = params;
    const pageContext = SubmitMessagePartHandler.buildPageContext(
      submission.pageContext,
    );

    return {
      id: submission.id,
      participant_id: participant.id,
      participant_type: FrameworkAdapter.participantTypeFromRole(
        participant.role,
      ),
      parts: submission.parts as ConversationsApiDataMessagePart[],
      ...(submission.creatorPartId
        ? {creator_part_id: submission.creatorPartId}
        : {}),
      ...(submission.dataWithPii
        ? {data_with_pii: submission.dataWithPii}
        : {}),
      ...(pageContext ? {page_context: pageContext} : {}),
    };
  }

  private static buildPageContext(
    pageContext?: Record<string, unknown>,
  ): ConversationsApiSendMessageEndpointRequest['page_context'] {
    if (!pageContext) {
      return undefined;
    }

    const normalized =
      PageContextNormalizer.normalizePageContextForMessage(pageContext);
    return Object.keys(normalized).length > 0
      ? (normalized as ConversationsApiSendMessageEndpointRequest['page_context'])
      : undefined;
  }
}
